import React, { PropsWithChildren, useRef, useEffect } from 'react'
import { View, ViewStyle, Platform, StyleSheet } from 'react-native'

import WebInfoColumn from './WebInfoColumn'
import Drawer from '../navigation/Drawer'
import { useWebLayout } from '../providers/WebLayoutProvider'
import appStyles from '../styles/app-styles'
import { findScrollableChild } from '../utils/findScrollableChild'

type WebScrollable = {
  scrollTop: number
  scrollHeight: number
  clientHeight: number
  addEventListener: (event: 'scroll', listener: () => void) => void
  removeEventListener: (event: 'scroll', listener: () => void) => void
}

const RootContainer: React.FC<
  PropsWithChildren<{ contentContainerStyle?: ViewStyle }>
> = ({ children, contentContainerStyle }) => {
  const centerWrapperRef = useRef<View>(null)
  const leftRef = useRef<View>(null)
  const rightRef = useRef<View>(null)

  const { webLayoutEnabled } = useWebLayout()

  useEffect(() => {
    if (Platform.OS !== 'web' || !webLayoutEnabled) return

    let removeListener: (() => void) | null = null
    // Wait for the FlatList to mount and render its scrollable container
    const timeout = setTimeout(() => {
      const wrapper = centerWrapperRef.current
      if (!wrapper) return

      const outerScrollable = findScrollableChild(wrapper)
      const innerScrollable = findScrollableChild(outerScrollable)
      const centerElement = (innerScrollable ??
        outerScrollable) as WebScrollable | null

      if (!centerElement) {
        console.warn('Could not find scrollable element inside FlatList')
        return
      }

      const onScroll = () => {
        const scrollTop = centerElement.scrollTop

        const leftElement = leftRef.current as WebScrollable | null
        const rightElelement = rightRef.current as WebScrollable | null

        if (leftElement) {
          const maxLeftScroll =
            leftElement.scrollHeight - leftElement.clientHeight
          leftElement.scrollTop = Math.min(scrollTop, maxLeftScroll)
        }

        if (rightElelement) {
          const maxRightScroll =
            rightElelement.scrollHeight - rightElelement.clientHeight
          rightElelement.scrollTop = Math.min(scrollTop, maxRightScroll)
        }
      }

      centerElement.addEventListener('scroll', onScroll)
      removeListener = () => {
        centerElement.removeEventListener('scroll', onScroll)
      }
    }, 0)

    return () => {
      clearTimeout(timeout)
      removeListener?.()
    }
  }, [webLayoutEnabled])

  return (
    <View style={[appStyles.fullSize, appStyles.row]}>
      <View ref={leftRef} style={webLayoutEnabled && styles.scrollableSide}>
        <Drawer />
      </View>
      <View
        ref={centerWrapperRef}
        style={[appStyles.fullSize, contentContainerStyle]}>
        {children}
      </View>
      <View ref={rightRef} style={webLayoutEnabled && styles.scrollableSide}>
        <WebInfoColumn />
      </View>
    </View>
  )
}

export default RootContainer

const styles = StyleSheet.create({
  scrollableSide: {
    // @ts-expect-error
    overflowY: 'scroll',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
  },
})
